// Responsive three-pane app shell — left nav rail, center content, right context panel.
//
// Breakpoints (see src/styles.css):
//   Desktop  >=1024px  — full three-pane CSS grid, everything always visible.
//   Tablet   768-1023px — rail stays put; the right context panel becomes a toggled
//                          overlay sheet (slides in from the right, backdrop behind it).
//   Mobile   <768px     — single column; a top app bar replaces the rail (hamburger
//                          opens the rail as a left drawer); the context panel becomes
//                          a full-screen sheet; Record moves into the app bar as a
//                          compact button.
//
// Drawer/sheet a11y: both the nav drawer and the context sheet trap focus while open,
// close on Escape and on backdrop click, and restore focus to the control that opened
// them. Toggle buttons expose aria-expanded + aria-controls per WAI-ARIA dialog pattern.
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { XMarkIcon, Bars3Icon, BugAntIcon } from '@heroicons/react/24/outline';
import { SearchInput, SearchSnippet } from './ui';
import { useSearch } from '../lib/queries';
import { panelsEnabled } from '../lib/featureFlags';
import { useFeatureEnabled } from '../lib/features';
import { useBugReport } from '../lib/bugReport';

const SEARCH_RESULTS_ID = 'global-search-results';
/** Dropdown is a command-palette preview, not a full results page — keep it short. */
const MAX_SEARCH_RESULTS = 8;

export type PanelTab = 'summary' | 'assistant' | 'action-items';

// AI-3: the WAVE1-18 handshake. Action items is still an AI feature with no real backend —
// see panel/ActionItems.tsx's file header and lib/featureFlags.ts — so it stays gated behind
// `panelsEnabled()` and renders a "Coming soon" state when the flag flips it in. Summary
// (AI-1/AI-2) is real data, so it's always present: the whole context panel used to be gated
// off entirely (per web-ui-issues.md #18) because every tab was fabricated; now the panel
// itself always renders — only the still-fabricated tab stays behind the flag. Assistant
// (`POST /assistant/ask`) is real too but ships behind its own runtime switch,
// `useFeatureEnabled('assistant')` (FEATURES-1: the admin portal's switch; off → tab hidden).
// Computed by a function (not a module-level constant) so it re-evaluates `panelsEnabled()`
// on every render — `panelsEnabled()` reads `import.meta.env.VITE_FEATURE_PANELS`, which
// tests flip per-case via `vi.stubEnv`; a constant frozen at module-import time would never
// see that.
function panelTabs(assistantOn: boolean): { id: PanelTab; label: string }[] {
  return [
    { id: 'summary', label: 'Summary' },
    ...(assistantOn ? ([{ id: 'assistant', label: 'Assistant' }] as const) : []),
    ...(panelsEnabled() ? ([{ id: 'action-items', label: 'Action items' }] as const) : []),
  ];
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

/** Traps Tab/Shift+Tab focus within `ref` while `active`, closes on Escape, and restores
 * focus to whatever was focused before opening once `active` goes false. */
function useFocusTrap(active: boolean, ref: React.RefObject<HTMLElement>, onClose: () => void) {
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const node = ref.current;
    const focusable = node?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    focusable?.[0]?.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !node) return;
      const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => el.offsetParent !== null,
      );
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      previouslyFocused.current?.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
}

export interface NavItem {
  id: string;
  label: string;
  icon?: ReactNode;
  count?: number;
  active?: boolean;
  onClick: () => void;
}

export interface FolderItem {
  id: string;
  name: string;
  color: string;
  active: boolean;
  onClick: () => void;
}

/** Rail "Tags" section row (SHARE-5) — parallel to `FolderItem` but with no color dot,
 * rendered as `#name` to match the tag chips used elsewhere in the app. */
export interface TagItem {
  id: string;
  name: string;
  active: boolean;
  onClick: () => void;
}

export function AppShell({
  navItems,
  folders,
  tags,
  userDisplay,
  userSub,
  userInitials,
  onSignOut,
  onRecord,
  onImport,
  onOpenRecording,
  children,
  panelTab,
  onPanelTabChange,
  panelContent,
}: {
  navItems: NavItem[];
  folders?: FolderItem[];
  tags?: TagItem[];
  userDisplay: string;
  userSub?: string;
  userInitials: string;
  onSignOut: () => void;
  onRecord: () => void;
  onImport?: () => void;
  /** Navigates to a recording's detail screen — wired to the global ⌘K search below. */
  onOpenRecording?: (id: string) => void;
  children: ReactNode;
  panelTab: PanelTab;
  onPanelTabChange: (tab: PanelTab) => void;
  panelContent: ReactNode;
}) {
  const bugReport = useBugReport();
  const assistantOn = useFeatureEnabled('assistant');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const drawerRef = useRef<HTMLDivElement>(null!);
  const panelRef = useRef<HTMLDivElement>(null!);
  const searchRef = useRef<HTMLDivElement>(null!);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useFocusTrap(drawerOpen, drawerRef, () => setDrawerOpen(false));
  useFocusTrap(panelOpen, panelRef, () => setPanelOpen(false));
  useFocusTrap(searchOpen, searchRef, () => setSearchOpen(false));

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const closePanel = useCallback(() => setPanelOpen(false), []);
  const closeSearch = useCallback(() => setSearchOpen(false), []);

  const search = useSearch(query);
  const searchResults = search.data?.items.slice(0, MAX_SEARCH_RESULTS);
  const hasQuery = query.trim().length > 0;

  const selectSearchResult = useCallback(
    (recordingId: string) => {
      onOpenRecording?.(recordingId);
      setQuery('');
      closeSearch();
    },
    [onOpenRecording, closeSearch],
  );

  // Global ⌘K / Ctrl+K shortcut — opens the search overlay and focuses its input from
  // anywhere in the shell, matching the ⌘K badge already shown next to the search bar.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
        searchInputRef.current?.focus();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  // Close the dropdown on an outside click — there's no full-screen backdrop for this
  // overlay (it sits inline under the search bar rather than as a scrim'd sheet).
  useEffect(() => {
    if (!searchOpen) return;
    function onPointerDown(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [searchOpen]);

  const railContent = (
    <nav className="rail" aria-label="Primary">
      <button
        className="brand"
        type="button"
        onClick={() => {
          navItems.find((n) => n.id === 'recordings')?.onClick();
          closeDrawer();
        }}
      >
        <img className="brand-mark" src="/scribe-icon-192.png" alt="" width={27} height={27} />
        Scribe
      </button>
      <div className="rail-cta">
        <button className="btn-rec" onClick={onRecord} style={{ flex: 1 }}>
          Record
        </button>
        {onImport && (
          <button className="ghost" style={{ flex: 1 }} onClick={onImport}>
            Import
          </button>
        )}
      </div>
      <div className="rail-nav">
        {navItems.map((item) => (
          <button
            key={item.id}
            type="button"
            className={'rail-link' + (item.active ? ' on' : '')}
            aria-current={item.active ? 'page' : undefined}
            onClick={() => {
              item.onClick();
              closeDrawer();
            }}
          >
            <span aria-hidden="true">{item.icon}</span>
            {item.label}
            {item.count != null && <span className="count">{item.count}</span>}
          </button>
        ))}
      </div>
      {/* SHARE-5: the rail's Folders/Tags rows are real filters for the main recordings
          view (single-select, toggle-to-clear — see App.tsx's Shell for the click wiring).
          Wrapped in `.rail-scroll` so a long folder/tag list scrolls within a capped
          height instead of pushing the rest of the rail (including sign-out) off-screen. */}
      {folders && folders.length > 0 && (
        <div>
          <div className="rail-section-label">Folders</div>
          <div className="rail-nav rail-scroll">
            {folders.map((f) => (
              <button
                key={f.id}
                type="button"
                className={'rail-link' + (f.active ? ' on' : '')}
                aria-current={f.active ? 'true' : undefined}
                onClick={() => {
                  f.onClick();
                  closeDrawer();
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 3,
                    background: f.color,
                    flex: 'none',
                  }}
                  aria-hidden="true"
                />
                {f.name}
              </button>
            ))}
          </div>
        </div>
      )}
      {tags && tags.length > 0 && (
        <div>
          <div className="rail-section-label">Tags</div>
          <div className="rail-nav rail-scroll">
            {tags.map((t) => (
              <button
                key={t.id}
                type="button"
                className={'rail-link' + (t.active ? ' on' : '')}
                aria-current={t.active ? 'true' : undefined}
                onClick={() => {
                  t.onClick();
                  closeDrawer();
                }}
              >
                #{t.name}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="rail-me">
        <div className="rail-me-identity">
          <span className="avatar" style={{ width: 30, height: 30, fontSize: 12 }}>
            {userInitials}
          </span>
          <div style={{ lineHeight: 1.25, minWidth: 0, flex: 1 }}>
            <div className="rail-me-name">{userDisplay}</div>
            {userSub && <div className="rail-me-sub">{userSub}</div>}
          </div>
        </div>
        {/* ERRORS-1 Part B: present only when bug reports are enabled (useBugReport is null
            otherwise). Lives in the rail, so the mobile drawer — which reuses railContent —
            gets it too. */}
        {bugReport && (
          <button
            type="button"
            className="ghost sm"
            style={{ width: '100%' }}
            onClick={() => bugReport.open()}
          >
            <BugAntIcon width={16} height={16} aria-hidden="true" />
            Report a bug
          </button>
        )}
        <button className="ghost sm" style={{ width: '100%' }} onClick={onSignOut}>
          Sign out
        </button>
      </div>
    </nav>
  );

  const panel = (
    <aside
      id="context-panel"
      className={'context-panel' + (panelOpen ? ' is-open' : '')}
      aria-label="Context panel"
      aria-hidden={undefined}
      ref={panelRef}
    >
      <div className="panel-tabs" role="tablist" aria-label="Context panel views">
        {panelTabs(assistantOn).map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={`panel-tab-${t.id}`}
            aria-selected={panelTab === t.id}
            aria-controls={`panel-tabpanel-${t.id}`}
            className={'panel-tab' + (panelTab === t.id ? ' on' : '')}
            onClick={() => onPanelTabChange(t.id)}
          >
            {t.label}
          </button>
        ))}
        <button
          type="button"
          className="icon-btn panel-close"
          aria-label="Close panel"
          onClick={closePanel}
        >
          <XMarkIcon width={20} height={20} aria-hidden="true" />
        </button>
      </div>
      <div
        className="panel-body"
        role="tabpanel"
        id={`panel-tabpanel-${panelTab}`}
        aria-labelledby={`panel-tab-${panelTab}`}
      >
        {panelContent}
      </div>
    </aside>
  );

  return (
    <div className="shell">
      {/* Mobile app bar */}
      <div className="shell-appbar">
        <button
          type="button"
          className="icon-btn"
          aria-label="Open navigation"
          aria-expanded={drawerOpen}
          aria-controls="nav-drawer"
          onClick={() => setDrawerOpen(true)}
        >
          <Bars3Icon width={20} height={20} aria-hidden="true" />
        </button>
        <span className="brand" style={{ padding: 0 }}>
          <img className="brand-mark" src="/scribe-icon-192.png" alt="" width={27} height={27} />
          Scribe
        </span>
        <button type="button" className="btn-rec sm" onClick={onRecord} aria-label="Record">
          Record
        </button>
      </div>

      {/* Desktop/tablet rail (hidden <768px, shown instead inside the drawer) */}
      {railContent}

      {/* Mobile nav drawer */}
      <div
        id="nav-drawer"
        className={'nav-drawer' + (drawerOpen ? ' is-open' : '')}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        ref={drawerRef}
      >
        {drawerOpen && railContent}
      </div>

      <div className="shell-center">
        <div className="shell-topbar">
          <div className="topbar-search" ref={searchRef}>
            <SearchInput
              value={query}
              onChange={(v) => {
                setQuery(v);
                setSearchOpen(true);
              }}
              onClick={() => setSearchOpen(true)}
              inputRef={searchInputRef}
              placeholder="Search across every meeting, decision, and speaker…"
              ariaLabel="Search across every meeting, decision, and speaker"
              shortcut="⌘K"
              expanded={searchOpen}
              controlsId={SEARCH_RESULTS_ID}
            />
            {searchOpen && (
              <div className="search-panel" id={SEARCH_RESULTS_ID} role="listbox" aria-label="Search results">
                {!hasQuery ? (
                  <p className="search-panel-hint">Type to search meetings, decisions, and speakers…</p>
                ) : search.isLoading ? (
                  <p className="search-panel-hint">Searching…</p>
                ) : search.isError ? (
                  <p className="search-panel-hint">Search failed. Try again.</p>
                ) : !searchResults || searchResults.length === 0 ? (
                  <p className="search-panel-hint">No matches for “{query}”.</p>
                ) : (
                  <ul className="search-panel-list">
                    {searchResults.map((r) => (
                      <li key={r.segment_id}>
                        <button
                          type="button"
                          className="search-panel-item"
                          role="option"
                          aria-selected={false}
                          onClick={() => selectSearchResult(r.recording_id)}
                        >
                          <span className="search-panel-item-title">
                            {r.recording_title ?? 'Untitled recording'}
                          </span>
                          <span className="search-panel-item-snippet">
                            <SearchSnippet snippet={r.snippet} />
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            className="ghost panel-toggle"
            aria-expanded={panelOpen}
            aria-controls="context-panel"
            onClick={() => setPanelOpen((v) => !v)}
          >
            Summary
          </button>
        </div>
        <div className="shell-content">{children}</div>
      </div>

      {panel}

      {/* Backdrop shared by the nav drawer and the tablet/mobile context sheet */}
      <div
        className={'shell-scrim' + (drawerOpen || panelOpen ? ' is-visible' : '')}
        onClick={() => {
          closeDrawer();
          closePanel();
        }}
        aria-hidden="true"
      />
    </div>
  );
}
